import { useCallback, useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { DateOnlyField } from '../ui/DateOnlyField';
import { useAuth } from '../../contexts/AuthContext';
import {
  certificateDisplayLabel,
  deactivateCertificateFile,
  getCertificateSignedUrl,
  isCertificateExpired,
  listCompanyCertificateFiles,
  slugifyCompanyCertType,
  uploadCertificateFile,
  type CertificateFileRow,
} from '../../lib/certificateLibrary';
import { singaporeTodayDateString } from '../../utils/dateTime';

type EditorMode = 'closed' | 'add' | 'update';

type Props = {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
};

function formatValidity(row: CertificateFileRow): string {
  const from = row.valid_from?.trim();
  const until = row.valid_until?.trim() || '—';
  return from ? `${from} → ${until}` : until;
}

function addYearsToIsoDate(isoDate: string, years: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return '';
  const y = Number(m[1]) + years;
  const month = m[2];
  const day = Number(m[3]);
  const lastDay = new Date(Date.UTC(y, Number(month), 0)).getUTCDate();
  const safeDay = Math.min(day, lastDay);
  return `${y}-${month}-${String(safeDay).padStart(2, '0')}`;
}

export function PurchasingCompanyCertModal({ open, onClose, onChanged }: Props) {
  const { profile, authUser } = useAuth();
  const uploader =
    (profile as { display_name?: string; username?: string } | null)?.display_name?.trim() ||
    (profile as { username?: string } | null)?.username?.trim() ||
    authUser?.email?.trim() ||
    null;

  const [rows, setRows] = useState<CertificateFileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewBusyId, setPreviewBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [editorMode, setEditorMode] = useState<EditorMode>('closed');
  const [editingRow, setEditingRow] = useState<CertificateFileRow | null>(null);
  const [certName, setCertName] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listCompanyCertificateFiles());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load certificates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    setError(null);
    setEditorMode('closed');
    setEditingRow(null);
    setCertName('');
    setFile(null);
    void load();
  }, [open, load]);

  function closeEditor() {
    setEditorMode('closed');
    setEditingRow(null);
    setCertName('');
    setFile(null);
  }

  function openAdd() {
    setEditorMode('add');
    setEditingRow(null);
    setCertName('');
    const from = singaporeTodayDateString();
    setValidFrom(from);
    setValidUntil(addYearsToIsoDate(from, 5));
    setFile(null);
    setError(null);
    setMessage(null);
  }

  function openUpdate(row: CertificateFileRow) {
    setEditorMode('update');
    setEditingRow(row);
    setCertName(certificateDisplayLabel(row.cert_type, row.cert_label));
    const from = row.valid_from?.trim() || singaporeTodayDateString();
    setValidFrom(from);
    setValidUntil(row.valid_until?.trim() || addYearsToIsoDate(from, 5));
    setFile(null);
    setError(null);
    setMessage(null);
  }

  async function preview(row: CertificateFileRow) {
    if (!row.storage_path) return;
    setPreviewBusyId(row.id);
    setError(null);
    try {
      const url = await getCertificateSignedUrl(row.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open preview.');
    } finally {
      setPreviewBusyId(null);
    }
  }

  async function save() {
    const label = certName.trim();
    if (editorMode === 'add' && !label) {
      setError('Enter the certificate name / type.');
      return;
    }
    const certType =
      editorMode === 'update' && editingRow
        ? editingRow.cert_type
        : slugifyCompanyCertType(label);
    if (!certType) {
      setError('Enter a valid certificate name (e.g. SFA, ISO 22000, Halal).');
      return;
    }
    if (!file) {
      setError('Choose a PDF to upload.');
      return;
    }
    if (!validUntil.trim()) {
      setError('Validity end date is required.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await uploadCertificateFile({
        cert_type: certType,
        cert_label:
          editorMode === 'update' && editingRow
            ? certificateDisplayLabel(editingRow.cert_type, editingRow.cert_label)
            : label,
        valid_from: validFrom || null,
        valid_until: validUntil,
        file,
        uploaded_by: uploader,
      });
      closeEditor();
      await load();
      onChanged?.();
      setMessage(`${certificateDisplayLabel(saved.cert_type, saved.cert_label)} saved.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: CertificateFileRow) {
    const label = certificateDisplayLabel(row.cert_type, row.cert_label);
    if (!window.confirm(`Remove ${label}?`)) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await deactivateCertificateFile(row.id);
      if (editingRow?.id === row.id) closeEditor();
      await load();
      onChanged?.();
      setMessage('Certificate removed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remove failed.');
    } finally {
      setBusy(false);
    }
  }

  const editorOpen = editorMode !== 'closed';
  const editorTitle =
    editorMode === 'update'
      ? `Update · ${certificateDisplayLabel(editingRow?.cert_type ?? '', editingRow?.cert_label)}`
      : 'Add company certificate';

  return (
    <>
      <Modal
        title="Company-wide certificates"
        open={open}
        onClose={onClose}
        extraWide
        compactHeader
        panelClassName="purch-cert-modal"
        headerActions={
          <div className="purch-cert-modal__header-actions">
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy || editorOpen}
              onClick={openAdd}
            >
              Add
            </button>
          </div>
        }
      >
        <div className="purch-cert-modal__body">
          {error && !editorOpen ? <p className="alert alert-error">{error}</p> : null}
          {message ? <p className="alert alert-success">{message}</p> : null}
          {loading ? <p className="page-lead">Loading…</p> : null}

          {!loading ? (
            <div className="purch-cert-modal__table-wrap table-wrap">
              <table className="data-table purch-cert-modal__table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Validity</th>
                    <th>File</th>
                    <th className="purch-cert-modal__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="purch-cert-modal__empty">
                        No company certificates yet. Click Add to upload one.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const expired = isCertificateExpired(row.valid_until);
                      const label = certificateDisplayLabel(row.cert_type, row.cert_label);
                      return (
                        <tr
                          key={row.id}
                          className={expired ? 'ctr-cert-lib__row--expired' : undefined}
                        >
                          <td>
                            <span className="purch-cert-modal__type">{label}</span>
                          </td>
                          <td>
                            <span className="purch-cert-modal__validity">{formatValidity(row)}</span>
                            {expired ? (
                              <span className="ctr-cert-lib__pill ctr-cert-lib__pill--expired">
                                Expired
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {row.storage_path && row.file_name ? (
                              <button
                                type="button"
                                className="purch-cert-modal__file-link"
                                disabled={previewBusyId === row.id}
                                onClick={() => void preview(row)}
                              >
                                {previewBusyId === row.id ? 'Opening…' : row.file_name}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            <div className="purch-cert-modal__row-actions">
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={busy}
                                onClick={() => openUpdate(row)}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={busy}
                                onClick={() => void remove(row)}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        title={editorTitle}
        open={open && editorOpen}
        onClose={closeEditor}
        nested
        compactHeader
        panelClassName="purch-cert-editor-modal"
      >
        <div className="purch-cert-editor">
          {error ? <p className="alert alert-error">{error}</p> : null}
          <p className="purch-cert-editor__hint">
            Enter any company certificate name. Valid until defaults to Valid from + 5 years.
          </p>
          <div className="purch-cert-editor__grid">
            <label className="field purch-cert-editor__file">
              <span className="field-label">Certificate</span>
              <input
                type="text"
                className="field-input"
                value={certName}
                disabled={busy || editorMode === 'update'}
                placeholder="e.g. SFA, ISO 22000, Halal, BRC…"
                onChange={(e) => setCertName(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="field">
              <span className="field-label">Valid from</span>
              <DateOnlyField
                value={validFrom}
                disabled={busy}
                onChange={(next) => {
                  setValidFrom(next);
                  const auto = addYearsToIsoDate(next, 5);
                  if (auto) setValidUntil(auto);
                }}
              />
            </label>
            <label className="field">
              <span className="field-label">Valid until</span>
              <DateOnlyField
                value={validUntil}
                disabled={busy}
                onChange={setValidUntil}
              />
            </label>
            <label className="field purch-cert-editor__file">
              <span className="field-label">PDF</span>
              <div className="purch-cert-editor__file-box">
                <input
                  type="file"
                  className="purch-cert-editor__file-input"
                  accept="application/pdf,.pdf"
                  disabled={busy}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <span className="purch-cert-editor__file-btn" aria-hidden>
                  Choose PDF
                </span>
                <span className="purch-cert-editor__file-name">
                  {file?.name ?? 'No file chosen'}
                </span>
              </div>
            </label>
          </div>
          <div className="purch-cert-editor__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy}
              onClick={closeEditor}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save PDF'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
